import moment from "moment";
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useState } from "react";
import { Calendar, momentLocalizer, SlotInfo } from "react-big-calendar";

import {
  CalendarEventDto,
  GetCalendarEventsInRangeRequest,
} from "../../interfaces";
import { ToastContainer } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { AddEditCalendarEventModal } from "./AddEditCalendarEventModal";
import { getStartAndEndOfCurrentMonth } from "../../services";
import { useLazyGetAllCalendarEventsQuery } from "../../store/api/calendarApi";

const localizer = momentLocalizer(moment);

export const AcademicCalendarPage = () => {
  //#region hooks
  const { t, i18n } = useTranslation();
  //#endregion

  document.title = `${t("documentTitle", {
    pageName: "controlAppAcademicCalendar",
  })}`;

  //#region internal state
  const [events, setEvents] = useState<any[]>([]);
  const [showAddEditEventModal, setShowAddEditEventModal] =
    useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<any>();
  const [range, setRange] = useState<GetCalendarEventsInRangeRequest>();
  //#endregion

  //#region RTK Query hooks
  const [getAllCalendarEvents, { data: eventsResponse }] =
    useLazyGetAllCalendarEventsQuery();
  //#endregion

  //#region effects

  useEffect(() => {
    const { startOfMonth, endOfMonth } = getStartAndEndOfCurrentMonth();
    setRange({ from: startOfMonth, to: endOfMonth });
  }, []);

  useEffect(() => {
    if (range?.from && range?.to) getAllCalendarEvents(range);
  }, [range]);

  useEffect(() => {
    if (eventsResponse) {
      const responseData = eventsResponse;
      const eventsArray = Array.isArray(responseData) ? responseData : [];
      setEvents(
        eventsArray.map((e: CalendarEventDto) => {
          return {
            id: e.id,
            title: i18n.language === "ar" ? e.title.ar : e.title.en,
            allDay: e.allDay,
            end: new Date(e.endAt),
            start: new Date(e.startAt),
          };
        }),
      );
    }
  }, [eventsResponse, i18n.language]);

  //#endregion

  //#region handlers
  const handleSelectSlot = useCallback(
    (event: SlotInfo) => {
      const { slots } = event;
      let startAt: Date;
      let endAt: Date;
      let allDay: boolean = false;
      if (slots.length == 1) {
        startAt = new Date(slots[0]);
        endAt = new Date(slots[0]);
        allDay = true;
        startAt.setHours(0, 0, 0, 0);
        endAt.setHours(23, 59, 59, 999);
      } else {
        startAt = new Date(slots[0]);
        endAt = new Date(slots[slots.length - 1]);
        const isBothMidnight =
          startAt.getHours() === 0 &&
          startAt.getMinutes() === 0 &&
          endAt.getHours() === 0 &&
          endAt.getMinutes() === 0;
        if (isBothMidnight) {
          startAt.setHours(0, 0, 0, 0);
          endAt.setHours(23, 59, 59, 999);
        }
        allDay = false;
      }
      setSelectedEvent({ startAt, endAt, allDay });
      setShowAddEditEventModal(true);
    },
    [setEvents],
  );

  const handleSelectEvent = (event: any) => {
    setShowAddEditEventModal(true);
    const responseData = eventsResponse;
    const eventsArray = Array.isArray(responseData) ? responseData : [];
    const eventToModify = eventsArray.find(
      (i: CalendarEventDto) => i.id === event.id,
    );
    setSelectedEvent(eventToModify);
  };

  const fetchEventsForRange = useCallback(
    (range: Date[] | { start: Date; end: Date }) => {
      if (Array.isArray(range)) {
        const _from = range[0];
        const _to = range[range.length - 1];
        _from.setHours(0, 0, 0, 0);
        _to.setHours(23, 59, 59, 999);
        setRange({ from: _from, to: _to });
      } else {
        setRange({ from: range.start, to: range.end });
      }
    },
    [],
  );

  //#endregion

  return (
    <Row>
      <Col>
        <Card>
          <CardHeader>
            <h6 className="card-title mb-0">{t("academicCalendar")}</h6>
          </CardHeader>
          <CardBody>
            <div className="vh-100 mt-4">
              <Calendar
                culture={i18n.language === "ar" ? "ar-AE" : "en"}
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                onRangeChange={fetchEventsForRange}
                selectable
                messages={
                  i18n.language === "ar"
                    ? {
                        week: t("week"),
                        work_week: t("workWeek"),
                        day: t("day"),
                        month: t("month"),
                        previous: t("previous"),
                        next: t("next"),
                        today: t("today"),
                        agenda: t("event"),
                        allDay: t("allDay"),
                        noEventsInRange: t("noEventsInRange"),
                        date: t("date"),
                        event: t("event"),
                        time: t("time"),
                        tomorrow: t("tomorrow"),
                        yesterday: t("yesterday"),
                        showMore: (total) => `+${total} ${t("total")}`,
                      }
                    : {}
                }
                rtl={i18n.language === "ar"}
                popup
              />
            </div>
            <AddEditCalendarEventModal
              calendarEvent={selectedEvent}
              show={showAddEditEventModal}
              onCloseClick={() => {
                setShowAddEditEventModal(false);
                setSelectedEvent(null);
                if (range?.from && range?.to) {
                  getAllCalendarEvents(range);
                }
              }}
            />
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};
